import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@workos-inc/authkit-react";
import * as Sentry from "@sentry/react";
import { ApiError, apiFetch, OfflineError } from "@/lib/api";
import {
  planKeys,
  projectKeys,
  recordKeys,
} from "@/lib/query-keys";
import { invalidateReportContents } from "@/lib/invalidate-report-contents";
import {
  findPendingRecord,
  putPendingRecord,
  updatePendingRecordFields,
  type PendingIssue,
  type PendingPhoto,
  type PendingRecord,
} from "@/offline/db";
import { patchPreconSelectionPin } from "@/components/reports/precon-report-builder/precon-handoff";
import type { ProjectMetadataField, ProjectRecord } from "@/types/api";
import type { CapturedPhoto } from "@/components/common/record-capture/captured-photo";
import type { Drawing } from "@/components/records/image-markup-editor/types";
import type { CropRect } from "@/components/records/image-markup-editor/crop-overlay";

interface AnnotationPayload {
  drawings: Drawing[];
  crop?: CropRect;
  /** Clockwise quarter-turns applied before crop/drawings; omitted when 0. */
  rotation?: number;
  downscale_max_px?: number;
}

interface SubmitRecordParams {
  originalFile: File;
  /**
   * Client-rendered annotated bytes. Uploaded as the multipart `image`
   * field; the server skips its render path when the `original` field is
   * also present (see [[photo-upload-flow.spec]]).
   */
  annotatedBlob: Blob;
  projectId: string;
  planId?: string | null;
  pinX?: number | null;
  pinY?: number | null;
  /** Plan-relative bearing in degrees `[0, 360)`; null for an undirected pin. */
  pinBearing?: number | null;
  metadataFields: ProjectMetadataField[];
  tags?: string[];
  annotations: AnnotationPayload;
  takenAtHint?: string | null;
  /**
   * Raise this issue on the same request that creates the record. Queued with
   * the record, so it survives being captured offline like everything else.
   */
  issue?: PendingIssue | null;
  data: Record<string, unknown>;
  onSuccess: () => void;
  onError?: (error: string) => void;
}

interface SubmitMultiRecordParams {
  projectId: string;
  /**
   * The captured batch (>1 photo). Each becomes a {@link PendingPhoto} on the
   * single queued record; `photos[0]` is the cover whose bytes/annotations are
   * mirrored to the entry's top level so the existing tile + modal-open code
   * render it with no special-casing.
   */
  capturedPhotos: CapturedPhoto[];
  planId?: string | null;
  pinX?: number | null;
  pinY?: number | null;
  /** Plan-relative bearing in degrees `[0, 360)`; null for an undirected pin. */
  pinBearing?: number | null;
  metadataFields: ProjectMetadataField[];
  tags?: string[];
  /**
   * Raise this issue on the same request that creates the record. Queued with
   * the record, so it survives being captured offline like everything else.
   */
  issue?: PendingIssue | null;
  data: Record<string, unknown>;
  onSuccess: () => void;
  onError?: (error: string) => void;
}

interface UpdateRecordParams {
  projectId: string;
  recordId: string;
  planId?: string | null;
  pinX?: number | null;
  pinY?: number | null;
  /** Plan-relative bearing in degrees `[0, 360)`; null for an undirected pin. */
  pinBearing?: number | null;
  metadataFields: ProjectMetadataField[];
  tags?: string[];
  data: Record<string, unknown>;
  onSuccess: () => void;
  onError?: (error: string) => void;
}

/**
 * Maps a single {@link CapturedPhoto} (from the in-app camera / markup
 * pipeline) to the {@link PendingPhoto} shape the outbox expects.
 *
 * Extracted so both {@link submitMultiRecord} and the pending-draft append
 * handler (`handleAppendToPendingDraft` in `image-viewer-modal.tsx`) use the
 * same mapping — avoiding a second hand-rolled mapping that could drift on the
 * casing footgun (`downscaleMaxPx` camel vs `downscale_max_px` snake).
 */
export function capturedToPendingPhoto(p: CapturedPhoto): PendingPhoto {
  return {
    photoId: p.id,
    annotatedBlob: p.annotatedBlob,
    originalBlob: p.originalFile,
    originalFileName: p.originalFile.name || null,
    annotations: {
      drawings: p.drawings,
      crop: p.crop,
      rotation: p.rotation,
      downscale_max_px: p.downscaleMaxPx,
    },
    takenAtHint: p.takenAtHint,
    uploadStatus: "pending",
  };
}

/**
 * Read one metadata value off a record by the FIELD'S NAME.
 *
 * The inverse of {@link buildMetadata}, and it exists because the two halves
 * disagree about keys in a way that is easy to get wrong: fields are addressed
 * by NAME in the UI ("the Remarks field") but stored by ID, because
 * `buildMetadata` writes `out[field.id]`. Looking a value up by name against
 * the stored object therefore finds nothing on every record the app itself
 * created — silently, since the field exists and the record has metadata.
 *
 * Returns undefined when no field carries that name, or the stored value is
 * not a string.
 */
export function readMetadataByFieldName(
  fields: ProjectMetadataField[],
  metadata: Record<string, unknown> | null | undefined,
  fieldName: string,
): string | undefined {
  const target = fieldName.toLowerCase();
  const field = fields.find((f) => f.name.toLowerCase() === target);
  if (!field) return undefined;
  const value = metadata?.[field.id];
  return typeof value === "string" ? value : undefined;
}

export function buildMetadata(
  fields: ProjectMetadataField[],
  data: Record<string, unknown>,
): Record<string, string | number | boolean | string[]> {
  const out: Record<string, string | number | boolean | string[]> = {};
  for (const field of fields) {
    const value = data[field.id];
    if (value === undefined || value === null || value === "") continue;
    if (field.field_type === "multi_select" && Array.isArray(value)) {
      out[field.id] = value as string[];
    } else if (field.field_type === "boolean") {
      out[field.id] = Boolean(value);
    } else if (field.field_type === "number") {
      out[field.id] = typeof value === "number" ? value : Number(value);
    } else {
      out[field.id] = String(value);
    }
  }
  return out;
}

export function useSubmitRecord() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();
  const { getAccessToken } = useAuth();

  const invalidateForProject = (projectId: string) => {
    void queryClient.invalidateQueries({
      queryKey: recordKeys.byProject(projectId),
    });
    void queryClient.invalidateQueries({
      queryKey: projectKeys.detail(projectId),
    });
    void queryClient.invalidateQueries({
      queryKey: planKeys.byProject(projectId),
    });
    // A report's tile is drawn from the record it holds.
    invalidateReportContents(queryClient, projectId);
  };

  /**
   * Assembles a `PendingRecord` from a pre-built `photos[]` array.
   *
   * The cover (`photos[0]`) is mirrored to the entry's top-level blob /
   * annotation / hint fields so the existing single-photo tile and
   * modal-open code render the cover with no special-casing (the INVARIANT
   * maintained by both `submitRecord` and `submitMultiRecord`). Record-level
   * fields (plan, pin, tags, metadata) are passed directly by the callers.
   *
   * Every caller MUST ensure `photos` is non-empty before calling — the
   * guard below is purely defensive.
   */
  function buildPendingEntry({
    clientUuid,
    projectId,
    photos,
    planId,
    pinX,
    pinY,
    pinBearing,
    tags,
    metadata,
    issue,
  }: {
    clientUuid: string;
    projectId: string;
    photos: PendingPhoto[];
    planId: string | null;
    pinX: number | null;
    pinY: number | null;
    pinBearing: number | null;
    tags: string[];
    metadata: Record<string, string | number | boolean | string[]>;
    issue?: PendingIssue | null;
  }): PendingRecord | null {
    const cover = photos[0];
    if (!cover) return null;

    return {
      clientUuid,
      projectId,
      // Cover mirror: top-level fields reflect photos[0] so the tile and
      // modal-open code render the cover without special-casing.
      annotatedBlob: cover.annotatedBlob,
      originalBlob: cover.originalBlob,
      originalFileName: cover.originalFileName,
      annotations: cover.annotations,
      takenAtHint: cover.takenAtHint,
      planId,
      pinX,
      pinY,
      pinBearing,
      tags,
      metadata,
      issue: issue ?? null,
      status: "pending",
      revision: 0,
      deleted: false,
      createdAt: Date.now(),
      photos,
    };
  }

  /**
   * Runs the shared outbox write lifecycle: try the IDB write, breadcrumb on
   * success, Sentry-capture + `onError` on failure, prefix-invalidate + `onSuccess`.
   *
   * `errorPrefix` lets callers customise the user-facing error string
   * ("photo" vs "photos").
   */
  async function drainToOutbox({
    entry,
    projectId,
    errorPrefix,
    onSuccess,
    onError,
  }: {
    entry: PendingRecord;
    projectId: string;
    errorPrefix: string;
    onSuccess: () => void;
    onError?: (error: string) => void;
  }): Promise<void> {
    try {
      await putPendingRecord(entry);
    } catch (error) {
      // The durable write failed — the capture is NOT safely queued. Report
      // it so the caller keeps the modal open; closing now would lose the
      // photo(s) with no record anywhere. A failed local write is silent
      // data loss, so it also goes to Sentry (additive to the user toast) —
      // see [[error-reporting]] "never log-and-swallow".
      console.error(`Queue ${errorPrefix} to outbox failed:`, error);
      Sentry.captureException(error, {
        level: "error",
        tags: { area: "outbox", phase: "idb-write" },
        contexts: {
          outbox: { clientUuid: entry.clientUuid, projectId },
        },
      });
      onError?.(
        error instanceof Error
          ? `Couldn't save ${errorPrefix} — ${error.message}`
          : `Couldn't save ${errorPrefix}. Try again.`,
      );
      return;
    }
    // The capture is now durably queued — leave a breadcrumb so any later
    // outbox error (a permanent upload failure, a stuck retry) shows the
    // trail back to where this record entered the outbox. IDs only — never
    // the photo(s), remarks, or metadata values.
    Sentry.addBreadcrumb({
      category: "outbox",
      level: "info",
      message: "outbox record enqueued",
      data: { clientUuid: entry.clientUuid, projectId },
    });
    // Invalidate the `["pendingRecords"]` PREFIX, not the per-project literal:
    // prefix invalidation still matches the grid's own
    // `["pendingRecords", projectId]` query AND the always-mounted outbox
    // drain's `["pendingRecords"]` query, so both refresh and the drain
    // immediately replays the freshly queued record.
    void queryClient.invalidateQueries({
      queryKey: ["pendingRecords"],
    });
    onSuccess();
  }

  /**
   * Queues a new record to the durable local outbox instead of POSTing.
   *
   * This never touches the network — it writes a `pendingRecords` IDB entry,
   * invalidates the pending-records query so the grid shows the "syncing"
   * tile, and resolves. Background sync (`useOutboxDrain`) owns the upload.
   * The `clientUuid` generated here is the record's identity for life —
   * local id and `X-Idempotency-Key` alike — so retry-forever and reconcile
   * races can never duplicate it.
   *
   * The IDB write is local and fast (no network), so it IS awaited: the
   * caller must keep the modal open until `onSuccess` fires. If the write
   * throws — storage quota exceeded, IndexedDB unavailable — the capture
   * would otherwise be silently lost behind a closed modal, so the failure
   * is surfaced via `onError` and the caller keeps the modal open for a
   * retry. See [[offline-record-outbox]] L1. The create path still has no
   * `isSubmitting` loading state: the write is fast enough to feel instant.
   *
   * The queued entry carries a `photos[]` of length 1 so that
   * `isMultiPhotoRecord()` returns `true` and the drain routes it through the
   * v2.0 endpoint. The single photo's fields are mirrored to the entry's top
   * level (cover invariant), so existing tile and modal-open code need no
   * changes. See Task 1.1 of the unify-creates-v2 refactor.
   */
  const submitRecord = async ({
    originalFile,
    annotatedBlob,
    projectId,
    planId,
    pinX,
    pinY,
    pinBearing,
    metadataFields,
    tags,
    annotations,
    takenAtHint,
    issue,
    data,
    onSuccess,
    onError,
  }: SubmitRecordParams) => {
    const photo: PendingPhoto = {
      photoId: crypto.randomUUID(),
      annotatedBlob,
      originalBlob: originalFile,
      originalFileName: originalFile.name || null,
      annotations: {
        drawings: annotations.drawings,
        crop: annotations.crop,
        rotation: annotations.rotation,
        downscale_max_px: annotations.downscale_max_px,
      },
      takenAtHint: takenAtHint ?? null,
      uploadStatus: "pending",
    };

    const entry = buildPendingEntry({
      clientUuid: crypto.randomUUID(),
      projectId,
      photos: [photo],
      planId: planId ?? null,
      pinX: pinX ?? null,
      pinY: pinY ?? null,
      pinBearing: pinBearing ?? null,
      tags: tags ?? [],
      metadata: buildMetadata(metadataFields, data),
      issue,
    });
    // Guard: photos is always [photo], so this is unreachable in practice.
    if (!entry) return;

    await drainToOutbox({ entry, projectId, errorPrefix: "photo", onSuccess, onError });
  };

  /**
   * Queues a new MULTI-photo record to the durable local outbox as ONE entry.
   *
   * Mirrors {@link submitRecord}'s durability discipline exactly — try the IDB
   * write, leave the "outbox record enqueued" breadcrumb, surface a failed
   * write via `onError` (plus a Sentry capture, since a dropped capture is
   * silent data loss), invalidate the `["pendingRecords"]` prefix so the grid
   * and the always-mounted drain both replay it, then `onSuccess`. The
   * difference is shape, not lifecycle: instead of a single top-level photo it
   * writes a `photos[]` array carrying every captured photo with its own
   * `uploadStatus`, all sharing this entry's `clientUuid` as their
   * `client_record_uuid`. The drain uploads each photo to the v2.0 endpoint
   * under that shared id; per-photo `photoId` (= `CapturedPhoto.id`) is the
   * `X-Idempotency-Key` so a retry can never duplicate a photo. See
   * [[offline-record-outbox]] (multi-photo subsection).
   *
   * INVARIANT: the entry's top-level `annotatedBlob` / `originalBlob` /
   * `originalFileName` / `annotations` / `takenAtHint` MIRROR the cover
   * (`photos[0]`) so the single-photo tile + modal-open code render the cover
   * with no changes. The record-level fields (`plan` / `pin` / `tags` /
   * `metadata`) come from the form via {@link buildMetadata}, exactly as the
   * single-photo path.
   *
   * Like {@link submitRecord} the IDB write is local and fast (no network), so
   * it IS awaited and the caller keeps the modal open until `onSuccess`; the
   * create path has no `isSubmitting` loading state because the write feels
   * instant.
   */
  const submitMultiRecord = async ({
    projectId,
    capturedPhotos,
    planId,
    pinX,
    pinY,
    pinBearing,
    metadataFields,
    tags,
    issue,
    data,
    onSuccess,
    onError,
  }: SubmitMultiRecordParams) => {
    const photos: PendingPhoto[] = capturedPhotos.map(capturedToPendingPhoto);

    const entry = buildPendingEntry({
      // This clientUuid IS the record's `client_record_uuid` for life — the
      // shared identity every photo uploads under, and the local id the grid /
      // modal / reconcile key off. Retry-forever + reconcile races can never
      // duplicate the record.
      clientUuid: crypto.randomUUID(),
      projectId,
      photos,
      planId: planId ?? null,
      pinX: pinX ?? null,
      pinY: pinY ?? null,
      pinBearing: pinBearing ?? null,
      tags: tags ?? [],
      metadata: buildMetadata(metadataFields, data),
      issue,
    });
    // A >1-photo batch is the only caller, so `entry` is always present;
    // the guard keeps the types honest.
    if (!entry) return;

    await drainToOutbox({ entry, projectId, errorPrefix: "photos", onSuccess, onError });
  };

  /**
   * Updates a record's metadata (plan, pin, tags, dynamic fields).
   *
   * Branches on whether the target is still a local draft:
   * - **Pending** (an outbox entry exists for `recordId` — a pending record's
   *   id IS its `clientUuid`) → the edit is written straight to the outbox
   *   entry via `updatePendingRecordFields`, which bumps `revision`. The
   *   background drain reads the entry at upload time, so it carries the
   *   latest version. No server call, works offline, no spinner.
   * - **Synced** → the existing server `PATCH`, unchanged. The caller gates
   *   this on connectivity (a synced record is online-only for edit/delete).
   */
  const updateRecord = async ({
    projectId,
    recordId,
    planId,
    pinX,
    pinY,
    pinBearing,
    metadataFields,
    tags,
    data,
    onSuccess,
    onError,
  }: UpdateRecordParams) => {
    // A pending record is a local draft — edit it in the outbox, no network.
    // An outbox that cannot be read answers "not a draft", so an online save of
    // a synced record is never blocked by a broken local database.
    const pending = await findPendingRecord(recordId);
    if (pending) {
      try {
        await updatePendingRecordFields(recordId, {
          planId: planId ?? null,
          pinX: pinX ?? null,
          pinY: pinY ?? null,
          pinBearing: pinBearing ?? null,
          tags: tags ?? [],
          metadata: buildMetadata(metadataFields, data),
        });
        // Prefix-invalidate so the grid's `["pendingRecords", projectId]`
        // query and the always-mounted drain's `["pendingRecords"]` query
        // both refresh — same pattern as the create path.
        void queryClient.invalidateQueries({ queryKey: ["pendingRecords"] });
        // Mirror the pin edit into any in-flight Pre-Condition Survey
        // hand-off, optimistically at the outbox write — the moment the rest
        // of the app treats a draft edit as done. A draft that later fails to
        // sync only mis-tallies a builder preview (generation is online-only
        // and the backend derives pins from the DB anyway). Inside the try:
        // a failed IDB write must not patch. See
        // [[precon-report-builder.spec]] § Selection hand-off.
        patchPreconSelectionPin(projectId, recordId, {
          plan: planId ?? null,
          pin_x: pinX ?? null,
          pin_y: pinY ?? null,
        });
        onSuccess();
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to update draft";
        console.error("Update pending record failed:", error);
        // A failed write to a PENDING (outbox) record is silent data loss —
        // the user's edit to a local draft was dropped. Report it (additive
        // to the toast) — see [[error-reporting]] "never log-and-swallow".
        Sentry.captureException(error, {
          level: "error",
          tags: { area: "outbox", phase: "idb-write" },
          contexts: {
            outbox: { clientUuid: recordId, projectId },
          },
        });
        onError?.(message);
      }
      return;
    }

    setIsSubmitting(true);
    try {
      const metadata = buildMetadata(metadataFields, data);
      const fields: Record<string, unknown> = {
        tags: tags ?? [],
        plan: planId ?? null,
        pin_x: pinX ?? null,
        pin_y: pinY ?? null,
        pin_bearing: pinBearing ?? null,
      };
      if (Object.keys(metadata).length > 0) {
        fields.metadata = metadata;
      }

      // `apiFetch` throws a typed `OfflineError` when the device is offline,
      // so the `catch` below can surface the "reconnect to edit" affordance
      // instead of a raw network error — see [[offline-record-outbox]] H3.
      await apiFetch<ProjectRecord>(
        `/projects/1.2/projects/${projectId}/records/${recordId}/`,
        getAccessToken,
        {
          method: "PATCH",
          body: JSON.stringify(fields),
        },
      );

      // The server confirmed the plan/pin change — mirror it into any
      // in-flight Pre-Condition Survey hand-off so the builder reflects the
      // new pin state without reselecting (see [[precon-report-builder.spec]]
      // § Selection hand-off). Best-effort: no-ops when no hand-off exists.
      patchPreconSelectionPin(projectId, recordId, {
        plan: planId ?? null,
        pin_x: pinX ?? null,
        pin_y: pinY ?? null,
      });

      invalidateForProject(projectId);
      onSuccess();
    } catch (error) {
      // An `OfflineError` on the synced path is not a real failure — the
      // user just lost connectivity. Surface the reconnect affordance, never
      // a raw error string. The form keeps the entered edits.
      if (error instanceof OfflineError) {
        onError?.("Offline — reconnect to edit");
      } else {
        let message = "Failed to update record";
        if (error instanceof ApiError) {
          const detail = (error.body as { detail?: string } | null)?.detail;
          message = detail ?? `Update failed (${error.status})`;
        } else if (error instanceof Error) {
          message = error.message;
        }
        console.error("Update record failed:", error);
        onError?.(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    submitRecord,
    submitMultiRecord,
    updateRecord,
    isSubmitting,
  };
}
